import { computed, ref } from 'vue';
import useMainApiRequest from '@composables/useMainApiRequest';
import { registerCacheInvalidation } from '@composables/useCache';
import useModelMatrices, { type TmMatrix, invalidateMatrices, updateMatrixCache } from '@composables/useModelMatrices';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { get, has, set, unset } from 'lodash-es';
import { isCalibratingStatus, isCachingStatus } from './useStatus';

export { type TmMatrix } from '@composables/useModelMatrices';
export type TmCalibration = {
  id: number;
  name: string;
  description: string;
  datasetId: number | null;
  originalMatrixId: number | null;
  matrixId: number | null;
  epsilon: number | null;
  maxIter: number;
  maxIterCal: number;
  trafficModelId: number;
  createdAt: TmDate;
  owner: string | null;
  standardDeviations: number[] | null;
  status: string;
  fixedGenerators?: number[] | null;
};

export type TmCalibrationCreate = Pick<
  TmCalibration,
  'name' | 'description' | 'datasetId' | 'originalMatrixId' | 'epsilon' | 'maxIter' | 'maxIterCal' | 'fixedGenerators'
> & { calibratedMatrixName: string };

export type TmCalibrationUpdate = Pick<TmCalibration, 'name' | 'description'>;

const cachedCalibrations = ref<Record<TmCalibration['id'], TmCalibration>>({});
const isOverviewCached = ref(false);
const fetchCalibrationsPromise = ref<Promise<boolean | void>>(); // re-usable promise in case multiple components want to fetch calibrations at the same time

export default function useModelCalibrations() {
  const { makeRequest } = useMainApiRequest();
  const store = useStore();
  const { t } = useI18n();
  const matricesActions = useModelMatrices();

  const calibrations = computed(() => Object.values(cachedCalibrations.value));
  const trafficModelId = computed(() => store.state.scenarios.model.id);

  const getCalibrationById = (id: number) => get(cachedCalibrations.value, id);

  const updateCachedCalibration = (id: TmCalibration['id'], path: string, value: unknown) =>
    set(cachedCalibrations.value, path ? `${id}.${path}` : id, value);

  const fetchCalibrations = () => {
    if (isCacheAvailable()) return;
    if (fetchCalibrationsPromise.value) return fetchCalibrationsPromise.value;

    const fetchAction = () =>
      makeRequest({
        url: `calibrations/traffic-model/${trafficModelId.value}`,
        method: 'get',
        message: {
          error: {
            404: { summary: t('models.no calibrations found'), severity: 'info' },
            default: t('models.error while fetching calibrations'),
          },
        },
        onSuccess: ({ calibrations }: { calibrations: TmCalibration[] }) => {
          calibrations.forEach((cl) => set(cachedCalibrations.value, cl.id, cl));
          isOverviewCached.value = true;
        },
      });
    // re-use unresolved promise
    return (fetchCalibrationsPromise.value = fetchAction().then(
      () => (fetchCalibrationsPromise.value = undefined), // delete resolved promise
    ));
  };

  const fetchCalibration = (id: TmCalibration['id']) => {
    if (isCacheAvailable(id)) return;

    return makeRequest({
      url: `calibrations/${id}`,
      method: 'get',
      message: {
        error: t('models.error while fetching calibration'),
      },
      onSuccess: ({ calibration }: { calibration: TmCalibration }) =>
        set(cachedCalibrations.value, calibration.id, calibration),
      onFailure: () => invalidateCalibrations(id),
    });
  };

  const fetchCalibrationResults = async ({
    calibration,
    matrix,
    calibrations,
    matrices,
  }: {
    calibration?: TmCalibration;
    matrix?: TmMatrix;
    calibrations?: TmCalibration[];
    matrices?: TmMatrix[];
  } = {}) => {
    const isCalResolved = (c?: TmCalibration) => !!c && !isCalibratingStatus(c.status);
    const isMatResolved = (m?: TmMatrix) => !!m && !isCalibratingStatus(m.status) && !isCachingStatus(m.status);
    const isCalListResolved = !!calibrations && calibrations.every((c) => isCalResolved(c));
    const isMatListResolved = !!matrices && matrices.every((m) => isMatResolved(m));

    // If provided calibration/matrix/list is already in a resolved state -> no need to check for result
    if (isCalResolved(calibration) || isMatResolved(matrix) || isCalListResolved || isMatListResolved) return;

    if (calibration || matrix)
      return makeRequest({
        url: calibration ? `calibrations/${calibration?.id}/result` : `matrices/${matrix?.id}/result`,
        method: 'get',
        onSuccess: ({ calibration, matrix }: { calibration: TmCalibration; matrix: TmMatrix | null }) => {
          set(cachedCalibrations.value, calibration.id, calibration);
          if (matrix) updateMatrixCache(matrix.id, matrix);
        },
      });

    return makeRequest({
      url: `calibrations/traffic-model/${trafficModelId.value}/result`,
      method: 'get',
      onSuccess: ({ calibrations, matrices }: { calibrations: TmCalibration[]; matrices: TmMatrix[] }) => {
        calibrations.forEach((cl) => set(cachedCalibrations.value, cl.id, cl));
        matrices.forEach((mx) => updateMatrixCache(mx.id, mx));
        isOverviewCached.value = true;
      },
    });
  };

  const createCalibration = (calibration: TmCalibrationCreate) => {
    return makeRequest({
      url: `calibrations`,
      method: 'post',
      data: { ...calibration, trafficModelId: trafficModelId.value },
      message: {
        error: t('models.error while creating calibration'),
        success: {
          severity: 'info',
          summary: t('models.calibration info toast summary'),
          detail: t('models.calibration info toast detail'),
        },
      },
      onSuccess: ({ calibration, matrix }: { calibration: TmCalibration; matrix: TmMatrix | null }) => {
        set(cachedCalibrations.value, calibration.id, calibration);
        if (matrix) updateMatrixCache(matrix.id, matrix);
      },
    });
  };

  const updateCalibration = (id: TmCalibration['id'], calibration: TmCalibrationUpdate) => {
    return makeRequest({
      url: `calibrations/${id}`,
      method: 'patch',
      data: {
        name: calibration.name,
        description: calibration.description,
      },
      message: {
        error: t('models.error while updating calibration'),
        success: t('models.calibration updated'),
      },
      onSuccess: ({ calibration }: { calibration: TmCalibration }) =>
        set(cachedCalibrations.value, calibration.id, calibration),
    });
  };

  const deleteCalibration = (id: TmCalibration['id']) => {
    return makeRequest({
      url: `calibrations/${id}`,
      method: 'delete',
      message: {
        error: t('models.error while deleting calibration'),
        success: t('models.calibration deleted'),
      },
      onSuccess: () => unset(cachedCalibrations.value, id),
    });
  };

  const deleteMatrix = (matrixId: TmMatrix['id']) => {
    return makeRequest({
      url: `matrices/${matrixId}`,
      method: 'delete',
      message: {
        success: t('models.matrix deleted successfully'),
        error: t('models.error while deleting matrix'),
      },
      onSuccess: async () => {
        invalidateMatrices(matrixId);
        invalidateCalibrations(); // some calibrations might contain reference to this matrix -> invalidate them all (TODO: possible enhancement would be to return list of affected calibrations as a part of the request response and only update the cache here)
      },
    });
  };

  return {
    ...matricesActions,
    fetchCalibration,
    fetchCalibrationResults,
    fetchCalibrations,
    createCalibration,
    updateCalibration,
    updateCachedCalibration,
    deleteCalibration,
    calibrations,
    getCalibrationById,
    deleteMatrix,
    invalidateCalibrations,
  };
}

const isCacheAvailable = (id?: TmCalibration['id']) => {
  if (id) return has(cachedCalibrations.value, id);
  return isOverviewCached.value && !!Object.keys(cachedCalibrations.value).length;
};

export const invalidateCalibrations = (id?: TmCalibration['id']) => {
  if (id) return unset(cachedCalibrations.value, id);
  cachedCalibrations.value = {};
  isOverviewCached.value = false;
};

registerCacheInvalidation('calibrations', invalidateCalibrations);
